import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  createUserPrompt,
  extractFirstLineTitle,
  updateUserPrompt,
  type UserPrompt,
} from "@/lib/composerPrompts";
import { parseVariables } from "@/lib/composerPromptVariables";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  prompt?: UserPrompt | null;
  /** When true, focus the body textarea on open so ⌘V works immediately. */
  autoFocusBody?: boolean;
  onSaved?: (prompt: UserPrompt) => void;
};

const tagsToString = (tags: string[] | null | undefined): string =>
  (tags ?? []).join(", ");
const stringToTags = (value: string): string[] =>
  value
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);

export const PromptEditorDialog = ({
  open,
  onOpenChange,
  workspaceId,
  prompt,
  autoFocusBody,
  onSaved,
}: Props) => {
  const [title, setTitle] = useState(prompt?.title ?? "");
  const [body, setBody] = useState(prompt?.body ?? "");
  const [description, setDescription] = useState(prompt?.description ?? "");
  const [tagsInput, setTagsInput] = useState(tagsToString(prompt?.tags));
  const [modelHint, setModelHint] = useState(prompt?.model_hint ?? "");
  const [stripFirstLine, setStripFirstLine] = useState(false);
  const [saving, setSaving] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setTitle(prompt?.title ?? "");
    setBody(prompt?.body ?? "");
    setDescription(prompt?.description ?? "");
    setTagsInput(tagsToString(prompt?.tags));
    setModelHint(prompt?.model_hint ?? "");
    setStripFirstLine(false);
    if (autoFocusBody) {
      const t = setTimeout(() => bodyRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open, prompt, autoFocusBody]);

  const handleUseFirstLineAsTitle = () => {
    const candidate = extractFirstLineTitle(body);
    if (!candidate) {
      toast.error("Body is empty — nothing to use as a title.");
      return;
    }
    setTitle(candidate);
    toast.success("Title set from first line");
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!body.length) {
      toast.error("Body is empty");
      return;
    }
    setSaving(true);
    try {
      let finalTitle = title.trim();
      let finalBody = body;
      if (stripFirstLine) {
        const candidate = extractFirstLineTitle(body);
        if (candidate) {
          finalTitle = candidate;
          // Remove the first non-empty line from body, preserving everything else verbatim.
          const lines = body.split("\n");
          let removed = false;
          const kept: string[] = [];
          for (const line of lines) {
            if (!removed && line.trim().length > 0) {
              removed = true;
              continue;
            }
            kept.push(line);
          }
          finalBody = kept.join("\n").replace(/^\n+/, "");
        }
      }
      const payload = {
        title: finalTitle,
        body: finalBody, // verbatim — never trim or auto-format
        description: description.trim() || null,
        tags: stringToTags(tagsInput),
        model_hint: modelHint.trim() || null,
      };
      let saved: UserPrompt;
      if (prompt) {
        saved = await updateUserPrompt(prompt.id, payload);
      } else {
        saved = await createUserPrompt(workspaceId, payload);
      }
      toast.success(prompt ? "Prompt updated" : "Prompt saved");
      onSaved?.(saved);
      onOpenChange(false);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{prompt ? "Edit prompt" : "New prompt"}</DialogTitle>
          <DialogDescription>
            Paste your full prompt below. Whitespace, line breaks, and tags are preserved exactly as
            pasted — nothing is reformatted.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="pe-title">Title</Label>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 text-xs"
                onClick={handleUseFirstLineAsTitle}
              >
                Use first line as title
              </Button>
            </div>
            <Input
              id="pe-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Long-form essay outliner"
            />
            <label className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
              <Checkbox
                checked={stripFirstLine}
                onCheckedChange={(v) => setStripFirstLine(v === true)}
              />
              <span>Use first line as title and strip it from the body on save</span>
            </label>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="pe-body">Prompt body</Label>
            <Textarea
              id="pe-body"
              ref={bodyRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={20}
              spellCheck={false}
              className="font-mono text-xs leading-relaxed"
              placeholder="Paste the full prompt here…  Tip: use {topic} or {audience} to ask for fill-in values at run time."
            />
            {(() => {
              const vars = parseVariables(body);
              if (vars.length === 0) return null;
              return (
                <p className="text-[11px] text-muted-foreground">
                  Detected placeholders:{" "}
                  {vars.map((v, i) => (
                    <span key={v} className="font-mono">
                      {i > 0 ? ", " : ""}
                      {`{${v}}`}
                    </span>
                  ))}
                  . You'll be asked to fill these in when you Push &amp; Run.
                </p>
              );
            })()}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="pe-tags">Tags (optional, comma-separated)</Label>
              <Input
                id="pe-tags"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="essay, outline, claude"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="pe-model">Model hint (optional)</Label>
              <Input
                id="pe-model"
                value={modelHint}
                onChange={(e) => setModelHint(e.target.value)}
                placeholder="e.g., Claude 3.5 Sonnet"
              />
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="pe-desc">Description (optional)</Label>
            <Textarea
              id="pe-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="What this prompt does, when to use it…"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Save prompt
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PromptEditorDialog;
